import React, { useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';

import OrderShipmentFieldList from 'components/add_order/order/OrderDetail/OrderShipments/OrderShipmentFieldList';

import { shipmentApi } from 'api';
import {
  BEFORE_PACKED_SHIPMENT_STATUSES,
  PACKED_SHIPMENT_STATUSES,
  EDITABLE_SHIPMENT_STATUSES,
} from 'constants/shipment';

const OrderShipmentExpandedRow = ({ shipment }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shippingProviders, setShippingProviders] = useState([]);
  const [formData, setFormData] = useState({
    package_width: shipment.package_width,
    package_length: shipment.package_length,
    package_height: shipment.package_height,
    weight: shipment.weight,
    awb_number: shipment.awb_number,
    invoice_code: shipment.invoice_code,
    shipping_provider: shipment.shipping_provider,
  });

  const isShowEditButton = EDITABLE_SHIPMENT_STATUSES.includes(
    shipment.shipment_status,
  );

  const handleChange = e => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditClick = async () => {
    const isPacked = PACKED_SHIPMENT_STATUSES.includes(
      shipment.shipment_status,
    );

    if (isPacked) {
      try {
        setIsLoading(true);
        const response = await shipmentApi.getShippingProviders({
          is_active: true,
        });
        setShippingProviders(response || []);
      } catch (error) {
        toast.error('Failed to load shipping providers');
      } finally {
        setIsLoading(false);
      }
    }

    setIsEditing(true);
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const isBeforePacked = BEFORE_PACKED_SHIPMENT_STATUSES.includes(
        shipment.shipment_status,
      );
      const isPacked = PACKED_SHIPMENT_STATUSES.includes(
        shipment.shipment_status,
      );

      let requestBody = {};

      if (isBeforePacked) {
        requestBody = {
          package_width: formData.package_width,
          package_length: formData.package_length,
          package_height: formData.package_height,
          weight: formData.weight,
        };
      } else if (isPacked) {
        requestBody = {
          package_width: formData.package_width,
          package_length: formData.package_length,
          package_height: formData.package_height,
          weight: formData.weight,
          awb_number: formData.awb_number,
          invoice_code: formData.invoice_code,
          shipping_provider: formData.shipping_provider
            ? formData.shipping_provider.id
            : null,
        };
      }

      await shipmentApi.shipmentUpdate(shipment.id, requestBody);
      setIsEditing(false);
      toast.success('Shipment updated successfully');
    } catch (error) {
      toast.error('Failed to update shipment');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      package_width: shipment.package_width,
      package_length: shipment.package_length,
      package_height: shipment.package_height,
      weight: shipment.weight,
      awb_number: shipment.awb_number,
      invoice_code: shipment.invoice_code,
      shipping_provider: shipment.shipping_provider,
    });

    setIsEditing(false);
  };

  return (
    <div className="p-3 bg-light">
      <OrderShipmentFieldList
        shipment={shipment}
        isEditing={isEditing}
        formData={formData}
        handleChange={handleChange}
        shippingProviders={shippingProviders}
      />

      {shipment?.product_summary?.length ? (
        <div className="d-flex mt-3">
          <Table size="sm" className="w-auto">
            <thead className="bg-200">
              <tr>
                <th className="text-start pe-4">Item</th>
                <th className="text-start pe-4">SKU</th>
                <th className="text-end">Quantity</th>
              </tr>
            </thead>
            <tbody>
              {shipment?.product_summary.map(product => (
                <tr key={product.product_sku}>
                  <td className="text-start pe-4">{product.product_name}</td>
                  <td className="text-start pe-4">{product.product_sku}</td>
                  <td className="text-end">{product.quantity}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : null}

      {isShowEditButton && (
        <div className="d-flex justify-content-end gap-2 mt-3">
          {!isEditing ? (
            <Button
              variant="primary"
              size="sm"
              onClick={handleEditClick}
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Edit'}
            </Button>
          ) : (
            <>
              <Button variant="secondary" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderShipmentExpandedRow;
